import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import {
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdDeselect,
  MdOutlinePalette,
  MdSelectAll,
} from "react-icons/md";
import useModalsStore from "src/store/modalsStore";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import CheckBox from "../CheckBox/CheckBox";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import useCharts from "src/hooks/useCharts";
import Button from "../Button/Button";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "src/helpers/routes";
import useCollapsibles from "src/hooks/useCollapsibles";
import useMobile from "src/hooks/useMobile";
import RangeInput from "../RangeInput/RangeInput";
import CustomSelect, { ECustomSelectIconType } from "../Select/CustomSelect";
import { sortBy } from "lodash";
import SettingsPerDeviceInfoBlock from "../InfoBlock/SettingsPerDeviceInfoBlock/SettingsPerDeviceInfoBlock";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

const ChartSettingsModal: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { setShowChartSettingsModal } = useModalsStore();
  const { updateChart, chartsUiPreferences, currentChartsIcon } = useCharts();
  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();
  const navigate = useNavigate();

  const [showLegend, setShowLegend] = useState<boolean>(
    chartsUiPreferences.showLegend,
  );
  const [showTooltips, setShowTooltips] = useState<boolean>(
    chartsUiPreferences.showTooltips,
  );
  const [showToolbox, setShowToolbox] = useState<boolean>(
    chartsUiPreferences.showToolbox,
  );
  const [showAnimations, setShowAnimations] = useState<boolean>(
    chartsUiPreferences.showAnimations,
  );
  const [showZoom, setShowZoom] = useState<boolean>(
    chartsUiPreferences.showZoom,
  );
  const [showPositiveExpenses, setShowPositiveExpenses] = useState<boolean>(
    chartsUiPreferences.showPositiveExpenses,
  );
  const [showReferenceLine, setShowReferenceLine] = useState<boolean>(
    chartsUiPreferences.showReferenceLine,
  );
  const [incomeSymbolOpacity, setIncomeSymbolOpacity] = useState<number>(
    chartsUiPreferences.incomeSymbolOpacity,
  );
  const [incomeLineOpacity, setIncomeLineOpacity] = useState<number>(
    chartsUiPreferences.incomeLineOpacity,
  );
  const [expensesSymbolOpacity, setExpensesSymbolOpacity] = useState<number>(
    chartsUiPreferences.expensesSymbolOpacity,
  );
  const [expensesLineOpacity, setExpensesLineOpacity] = useState<number>(
    chartsUiPreferences.expensesLineOpacity,
  );
  const [incomeSymbol, setIncomeSymbol] = useState<string>(
    chartsUiPreferences.incomeSymbol,
  );
  const [expensesSymbol, setExpensesSymbol] = useState<string>(
    chartsUiPreferences.expensesSymbol,
  );

  const [incomeSymbolSize, setIncomeSymbolSize] = useState<number>(
    chartsUiPreferences.incomeSymbolSize || 35,
  );
  const [expensesSymbolSize, setExpensesSymbolSize] = useState<number>(
    chartsUiPreferences.expensesSymbolSize || 35,
  );

  useEffect(() => {
    updateChart({
      showLegend,
      showToolbox,
      showTooltips,
      showAnimations,
      showZoom,
      showPositiveExpenses,
      showReferenceLine,
      incomeSymbolOpacity,
      expensesSymbolOpacity,
      incomeLineOpacity,
      expensesLineOpacity,
      incomeSymbol,
      expensesSymbol,
      incomeSymbolSize,
      expensesSymbolSize,
    });
  }, [
    showLegend,
    showToolbox,
    showTooltips,
    showAnimations,
    showZoom,
    showPositiveExpenses,
    showReferenceLine,
    incomeSymbolOpacity,
    expensesSymbolOpacity,
    incomeLineOpacity,
    expensesLineOpacity,
    incomeSymbol,
    expensesSymbol,
    incomeSymbolSize,
    expensesSymbolSize,
  ]);

  const chartSymbols = useMemo(
    () => [
      ...sortBy(
        [
          {
            label: t("misc:charts.circle"),
            icon: "MdOutlineCircle",
            value: "circle",
            iconType: ECustomSelectIconType.ICON,
          },
          {
            label: t("misc:charts.rect"),
            icon: "MdOutlineSquare",
            value: "rect",
            iconType: ECustomSelectIconType.ICON,
          },
          {
            label: t("misc:charts.rect_rounded"),
            icon: "MdCropSquare",
            value: "roundRect",
            iconType: ECustomSelectIconType.ICON,
          },
          {
            label: t("misc:charts.triangle"),
            icon: "MdChangeHistory",
            value: "triangle",
            iconType: ECustomSelectIconType.ICON,
          },
          {
            label: t("misc:charts.diamond"),
            icon: "MdOutlineSquare",
            className: "rotate-45",
            value: "diamond",
            iconType: ECustomSelectIconType.ICON,
          },
          {
            label: t("misc:charts.arrow"),
            icon: "MdOutlineArrowUpward",
            value: "arrow",
            iconType: ECustomSelectIconType.ICON,
          },
        ],
        ["label"],
      ),
      {
        label: t("misc:charts.none"),
        icon: null,
        value: "none",
      },
    ],
    [t],
  );

  const isGeneralAllSelected = useMemo(() => {
    return (
      showLegend &&
      showTooltips &&
      showToolbox &&
      showAnimations &&
      showZoom &&
      showReferenceLine
    );
  }, [
    showLegend,
    showTooltips,
    showToolbox,
    showAnimations,
    showZoom,
    showReferenceLine,
  ]);

  const selectAllGeneral = () => {
    setShowLegend(true);
    setShowTooltips(true);
    setShowToolbox(true);
    setShowAnimations(true);
    setShowZoom(true);
    setShowReferenceLine(true);
  };

  const deselectAllGeneral = () => {
    setShowLegend(false);
    setShowTooltips(false);
    setShowToolbox(false);
    setShowAnimations(false);
    setShowZoom(false);
    setShowReferenceLine(false);
  };

  return (
    <Modal
      onEscape={() => setShowChartSettingsModal(false)}
      className={clsx(["md:!max-w-[600px]", "md:!w-full"])}
    >
      <Modal.Header
        onClose={() => setShowChartSettingsModal(false)}
        title={t("modals:chart_settings.title")}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full", "pb-2"])}>
          <SettingsPerDeviceInfoBlock />
          <CollapsibleBlock
            title={t("modals:chart_settings.collapsibles.general.title")}
            onChange={(isOpen: boolean) => {
              updateCollapsible({ chartPreferencesGeneralOpen: isOpen });
            }}
            // open={collapsiblePreferences.chartPreferencesGeneralOpen}
            blocked
            open
            icon={currentChartsIcon}
            buttons={[
              {
                onClick: () => selectAllGeneral(),
                tooltip: "Seleccionar todo",
                icon: <MdSelectAll />,
                hide: isGeneralAllSelected,
              },
              {
                onClick: () => deselectAllGeneral(),
                tooltip: "Deseleccionar todo",
                icon: <MdDeselect />,
                hide: !isGeneralAllSelected,
              },
            ]}
          >
            <div
              className={clsx([
                "grid",
                "w-full",
                "gap-2",
                "md:gap-1",
                "grid-cols-1",
                "md:grid-cols-2",
              ])}
            >
              <CheckBox
                checked={showLegend}
                onChange={setShowLegend}
                label={t(
                  "modals:chart_settings.collapsibles.general.legend.label",
                )}
                description={t(
                  "modals:chart_settings.collapsibles.general.legend.description",
                )}
              />
              <CheckBox
                checked={showTooltips}
                onChange={setShowTooltips}
                label={t(
                  "modals:chart_settings.collapsibles.general.tooltips.label",
                )}
                description={t(
                  "modals:chart_settings.collapsibles.general.tooltips.description",
                )}
              />
              <CheckBox
                checked={showToolbox}
                onChange={setShowToolbox}
                label={t(
                  "modals:chart_settings.collapsibles.general.toolbox.label",
                )}
                description={t(
                  "modals:chart_settings.collapsibles.general.toolbox.description",
                )}
              />
              <CheckBox
                checked={showAnimations}
                onChange={setShowAnimations}
                label={t(
                  "modals:chart_settings.collapsibles.general.animations.label",
                )}
                description={t(
                  "modals:chart_settings.collapsibles.general.animations.description",
                )}
              />
              <CheckBox
                checked={showZoom}
                onChange={setShowZoom}
                label={t(
                  "modals:chart_settings.collapsibles.general.zoom.label",
                )}
                description={t(
                  "modals:chart_settings.collapsibles.general.zoom.description",
                )}
              />
              {showTooltips && (
                <CheckBox
                  checked={showReferenceLine}
                  onChange={setShowReferenceLine}
                  label={t(
                    "modals:chart_settings.collapsibles.general.reference_line.label",
                  )}
                  description={t(
                    "modals:chart_settings.collapsibles.general.reference_line.description",
                  )}
                  disabled={!showTooltips}
                />
              )}
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("modals:chart_settings.collapsibles.income.title")}
            onChange={(isOpen: boolean) => {
              updateCollapsible({ chartPreferencesIncomeOpen: isOpen });
            }}
            // open={collapsiblePreferences.chartPreferencesIncomeOpen}
            blocked
            open
            icon={<MdArrowDownward size={ICON_SIZES.SM} />}
          >
            <div className={clsx(["grid", "grid-cols-1", "gap-2"])}>
              <div
                className={clsx([
                  "grid",
                  "w-full",
                  "gap-2",
                  "grid-cols-1",
                  "md:grid-cols-2",
                ])}
              >
                <RangeInput
                  label={t(
                    "modals:chart_settings.collapsibles.income.opacity.line",
                  )}
                  min={0.1}
                  max={1}
                  step={0.1}
                  defaultValue={incomeLineOpacity}
                  onChange={setIncomeLineOpacity}
                />
                <RangeInput
                  label={t(
                    "modals:chart_settings.collapsibles.income.opacity.symbol",
                  )}
                  min={0.1}
                  max={1}
                  step={0.1}
                  defaultValue={incomeSymbolOpacity}
                  onChange={setIncomeSymbolOpacity}
                />
                <CustomSelect
                  options={chartSymbols}
                  label={t(
                    "modals:chart_settings.collapsibles.income.symbol.shape",
                  )}
                  useValueAsLabel={false}
                  isClearable={false}
                  onChange={(e: any) => {
                    setIncomeSymbol(e?.value);
                  }}
                  withIcons
                  value={incomeSymbol}
                />
                <RangeInput
                  label={t(
                    "modals:chart_settings.collapsibles.income.symbol.size",
                  )}
                  min={10}
                  max={40}
                  step={1}
                  defaultValue={incomeSymbolSize}
                  onChange={setIncomeSymbolSize}
                  isPercentage={false}
                />
              </div>
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("modals:chart_settings.collapsibles.expenses.title")}
            onChange={(isOpen: boolean) => {
              updateCollapsible({ chartPreferencesExpensesOpen: isOpen });
            }}
            // open={collapsiblePreferences.chartPreferencesExpensesOpen}
            blocked
            open
            icon={<MdArrowUpward size={ICON_SIZES.SM} />}
          >
            <div className={clsx(["grid", "grid-cols-1", "gap-4"])}>
              <div className={clsx(["grid", "w-full", "gap-2", "grid-cols-1"])}>
                <div>
                  <CheckBox
                    checked={showPositiveExpenses}
                    onChange={setShowPositiveExpenses}
                    label={t(
                      "modals:chart_settings.collapsibles.expenses.label",
                    )}
                    description={t(
                      "modals:chart_settings.collapsibles.expenses.description",
                    )}
                  />
                  <p className={clsx(["md:text-sm", "text-xs", "hidden"])}>
                    {t("modals:chart_settings.collapsibles.expenses.note")}
                  </p>
                </div>
                <div
                  className={clsx([
                    "grid",
                    "w-full",
                    "gap-2",
                    "grid-cols-1",
                    "md:grid-cols-2",
                  ])}
                >
                  <RangeInput
                    label={t(
                      "modals:chart_settings.collapsibles.expenses.opacity.line",
                    )}
                    min={0.1}
                    max={1}
                    step={0.1}
                    defaultValue={expensesLineOpacity}
                    onChange={setExpensesLineOpacity}
                  />
                  <RangeInput
                    label={t(
                      "modals:chart_settings.collapsibles.expenses.opacity.symbol",
                    )}
                    min={0.1}
                    max={1}
                    step={0.1}
                    defaultValue={expensesSymbolOpacity}
                    onChange={setExpensesSymbolOpacity}
                  />
                  <CustomSelect
                    options={chartSymbols}
                    label={t(
                      "modals:chart_settings.collapsibles.income.symbol.shape",
                    )}
                    useValueAsLabel={false}
                    isClearable={false}
                    onChange={(e: any) => {
                      setExpensesSymbol(e?.value);
                    }}
                    withIcons
                    value={expensesSymbol}
                  />
                  <RangeInput
                    label={t(
                      "modals:chart_settings.collapsibles.income.symbol.size",
                    )}
                    min={10}
                    max={40}
                    step={1}
                    defaultValue={expensesSymbolSize}
                    onChange={setExpensesSymbolSize}
                    isPercentage={false}
                  />
                </div>
              </div>
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common:buttons.close"),
            onClick: () => setShowChartSettingsModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
          },
        ]}
        leftContent={
          <Button
            kind={EButtonKind.NORMAL}
            icon={<MdOutlinePalette size={ICON_SIZES.SM} />}
            onClick={() => {
              setShowChartSettingsModal(false);
              navigate(ROUTES.SETTINGS_APPEARANCE);
            }}
            tooltip={t("pages:settings.blocks.appearance.title")}
          />
        }
      />
    </Modal>
  );
};

export default ChartSettingsModal;
